import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateWebhookSubscriptionInput,
  UpdateWebhookSubscriptionInput,
  WebhookDeliveryResponse,
  WebhookSubscriptionResponse,
  WebhookSubscriptionWithSecretResponse,
} from '@orion/contracts';
import { api } from '../../../lib/api-client.js';

const KEY = ['admin-webhooks'];

export function useWebhookSubscriptionsQuery() {
  return useQuery({ queryKey: KEY, queryFn: () => api.get<WebhookSubscriptionResponse[]>('/admin/webhooks') });
}

export function useCreateWebhookSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWebhookSubscriptionInput) =>
      api.post<WebhookSubscriptionWithSecretResponse>('/admin/webhooks', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateWebhookSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateWebhookSubscriptionInput }) =>
      api.patch<WebhookSubscriptionResponse>(`/admin/webhooks/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRegenerateWebhookSecret() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<WebhookSubscriptionWithSecretResponse>(`/admin/webhooks/${id}/regenerate-secret`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteWebhookSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/admin/webhooks/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useWebhookDeliveriesQuery(id: string | null) {
  return useQuery({
    queryKey: ['admin-webhook-deliveries', id],
    queryFn: () => api.get<WebhookDeliveryResponse[]>(`/admin/webhooks/${id}/deliveries`),
    enabled: Boolean(id),
  });
}

export function useRedeliverWebhookDelivery(subscriptionId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deliveryId: string) => api.post<WebhookDeliveryResponse>(`/admin/webhooks/${subscriptionId}/deliveries/${deliveryId}/redeliver`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-webhook-deliveries', subscriptionId] }),
  });
}
